import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Shield,
  FileCheck,
  Fingerprint,
  Heart,
  CheckCircle,
  Clock,
  AlertCircle,
  Calendar,
  ArrowRight,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { PageHeader } from '@components/common/PageHeader';
import { getCurrentApplication, getApplicationSteps, LICENSE_TYPES, getMyLicenses } from '@mocks/licenses';

const getStatusIcon = (iconName: string) => {
  const icons: Record<string, React.ReactNode> = {
    FileText: <FileText className="h-5 w-5" />,
    Shield: <Shield className="h-5 w-5" />,
    FileCheck: <FileCheck className="h-5 w-5" />,
    Fingerprint: <Fingerprint className="h-5 w-5" />,
    Heart: <Heart className="h-5 w-5" />,
    CheckCircle: <CheckCircle className="h-5 w-5" />,
  };
  return icons[iconName] || <FileText className="h-5 w-5" />;
};

export const StatusTracker: React.FC = () => {
  const navigate = useNavigate();
  const application = getCurrentApplication();
  const licenses = getMyLicenses();

  if (!application) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Application Status"
          description="Track the progress of your license application"
        />
        
        <Card>
          <div className="text-center py-12">
            <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Application</h3>
            <p className="text-gray-600 mb-6">You don't have any active license applications.</p>
            <Button onClick={() => navigate('/apply')} icon={<FileText className="h-4 w-4" />}>
              Start New Application
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const steps = getApplicationSteps(application);
  const progress = (application.current_step / application.total_steps) * 100;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStepStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-600 bg-green-100';
      case 'IN_PROGRESS':
        return 'text-blue-600 bg-blue-100';
      case 'REJECTED':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-400 bg-gray-100';
    }
  };

  const getStepConnectorColor = (status: string) => {
    return status === 'COMPLETED' ? 'bg-green-500' : 'bg-gray-300';
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Application Status"
        description="Track the progress of your license application"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Application Overview */}
          <Card title="Application Overview" icon={<FileText className="h-5 w-5" />}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Application Type</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {LICENSE_TYPES[application.license_type]}
                  </p>
                </div>
                <Badge
                  variant={application.status === 'APPROVED' ? 'success' : 'warning'}
                  icon={<Clock className="h-4 w-4" />}
                >
                  {application.status.replace('_', ' ')}
                </Badge>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">Progress</p>
                  <p className="text-sm font-medium text-gray-900">
                    Step {application.current_step} of {application.total_steps}
                  </p>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-ncaa-primary h-3 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-sm text-gray-600">Submitted</p>
                  <p className="text-sm font-medium text-gray-900">
                    {application.submitted_at ? formatDate(application.submitted_at) : 'Not submitted'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(application.updated_at)}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Timeline */}
          <Card title="Application Timeline" icon={<Calendar className="h-5 w-5" />}>
            <div className="relative">
              {steps.map((step, index) => (
                <div key={step.id} className="relative pb-8 last:pb-0">
                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div
                      className={`absolute left-5 top-10 w-0.5 h-full ${getStepConnectorColor(
                        step.status
                      )}`}
                    />
                  )}

                  <div className="relative flex items-start gap-4">
                    {/* Icon */}
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${getStepStatusColor(
                        step.status
                      )}`}
                    >
                      {getStatusIcon(step.icon)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">{step.label}</h4>
                          <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                          {step.completed_at && (
                            <p className="text-xs text-gray-500 mt-1">
                              Completed: {formatDate(step.completed_at)}
                            </p>
                          )}
                        </div>
                        {step.status === 'COMPLETED' && (
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                        )}
                        {step.status === 'IN_PROGRESS' && (
                          <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 animate-pulse" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Current Status Card */}
          <Card>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Current Status</h3>
              <p className="text-sm text-gray-600 mb-4">
                {steps.find((s) => s.status === 'IN_PROGRESS')?.label || 'Processing'}
              </p>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">Estimated completion</p>
                <p className="text-sm font-medium text-gray-900 mt-1">2-3 business days</p>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card title="Quick Actions" icon={<ArrowRight className="h-5 w-5" />}>
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/documents')}
                icon={<FileText className="h-4 w-4" />}
              >
                View Documents
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/booking')}
                icon={<Calendar className="h-4 w-4" />}
              >
                Book Appointment
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/profile')}
                icon={<Shield className="h-4 w-4" />}
              >
                Update Profile
              </Button>
            </div>
          </Card>

          {/* Reviewer Notes */}
          {application.reviewer_notes && (
            <Card title="Reviewer Notes" icon={<AlertCircle className="h-5 w-5" />}>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">{application.reviewer_notes}</p>
              </div>
            </Card>
          )}

          {/* My Licenses */}
          {licenses.length > 0 && (
            <Card title="My Licenses" icon={<Shield className="h-5 w-5" />}>
              <div className="space-y-3">
                {licenses.map((license) => (
                  <div
                    key={license.id}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {LICENSE_TYPES[license.license_type]}
                      </p>
                      <Badge variant="success">Active</Badge>
                    </div>
                    <p className="text-xs text-gray-600">{license.license_number}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Expires: {license.expiry_date ? new Date(license.expiry_date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
